// Lib
import { useEffect, RefObject } from 'react';

// Utils
import { clearInboxReturnState, getInboxReturnState, saveInboxReturnState } from './inboxReturnStateService';
import findScrollableDomParent from '../../utils/dom/findScrollableDomParent';
import { PlatformDetails } from '../../../common/platform/platformTypes';
import { isDesktopMode } from '../../platform/utils/platformDetailsUtils';

/**
 * Restores the inbox return state when the component mounts and saves this state when the component unmounts.
 */
export default function useInboxReturnState(
    currentBoardId: string,
    inboxListContainerRef: RefObject<HTMLDivElement>,
    platformDetails: PlatformDetails,
): () => void {
    useEffect(() => {
        const handleBeforeUnload = () => {
            clearInboxReturnState();
        };

        const inboxReturnState = getInboxReturnState(currentBoardId);

        if (inboxReturnState) {
            // Delay the scroll restoration to the next frame to ensure that the inbox list container has been rendered
            requestAnimationFrame(() => {
                if (!inboxListContainerRef.current) return;

                if (isDesktopMode(platformDetails)) {
                    inboxListContainerRef.current.style.height = `${inboxReturnState.height}px`;
                }

                const scrollableSectionParent = findScrollableDomParent(inboxListContainerRef.current);

                if (scrollableSectionParent) {
                    scrollableSectionParent.scrollTop = inboxReturnState.scrollTop;
                }
            });
        }

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);

            if (!inboxListContainerRef?.current) return;

            const scrollableSectionParent = findScrollableDomParent(inboxListContainerRef.current);
            const scrollTop = scrollableSectionParent?.scrollTop || 0;

            const boundingClientRect = inboxListContainerRef.current.getBoundingClientRect();

            saveInboxReturnState(currentBoardId, { scrollTop, height: boundingClientRect.height });
        };
    }, []);

    /**
     * When the OrchestratedList finishes rendering all elements, we want to remove the height from the list
     * so that it will be rendered entirely based on the content, not the previously saved height of the list.
     */
    const cleanupOnOrchestrationEnd = () => {
        if (!inboxListContainerRef?.current) return;

        inboxListContainerRef.current.style.removeProperty('height');
    };

    return cleanupOnOrchestrationEnd;
}
